import styled from "styled-components";
import { Box, Flex, Text } from "rebass/styled-components";

export const ModalOverlay = styled(Flex)`
  align-items: center;
  background: rgba(0, 0, 0, 0.7);
  bottom: 0;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1000;
`;

export const ModalContainer = styled(Flex)`
  background: ${({ theme }) => theme.colors.ned};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.ice};
  flex-direction: column;
  max-height: 90vh;
  max-width: 1080px;
  overflow-y: auto;
  padding: 28px;
  position: relative;
  width: 90vw;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.chalkAlpha20};
    border-radius: 4px;
  }
`;

export const CloseButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: flex;
  justify-content: center;
  padding: 4px;
  position: absolute;
  right: 16px;
  top: 16px;

  &:hover {
    background: ${({ theme }) => theme.colors.chalkAlpha20};
    color: ${({ theme }) => theme.colors.ice};
  }
`;

export const Header = styled(Flex)`
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
`;

export const Title = styled(Box)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: "Barlow", sans-serif;
  font-size: 28px;
  font-weight: 600;
  margin: 0;
`;

export const GameLogo = styled.img`
  height: 26px;
  width: 26px;
`;

export const SettingRow = styled(Flex)<{ $disabled?: boolean }>`
  align-items: center;
  gap: 16px;
  justify-content: space-between;
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  padding: 14px 0;
  transition: opacity 0.15s ease;
  width: 100%;
`;

export const SettingInfo = styled(Flex)`
  flex-direction: column;
  gap: 2px;
`;

export const SettingLabel = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 17px;
  font-weight: 500;
  gap: 10px;
`;

export const SettingDescription = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-family: Barlow;
  font-size: 14px;
  padding-left: 0;
`;

export const ColorGrid = styled(Box)<{ $disabled?: boolean }>`
  display: flex;
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  transition: opacity 0.15s ease;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
`;

export const ColorSwatch = styled.button<{
  $swatch: string;
  $selected: boolean;
}>`
  background: ${({ $swatch }) => $swatch};
  border: 2px solid
    ${({ $selected, theme }) =>
      $selected ? theme.colors.envy : theme.colors.chalkAlpha20};
  border-radius: 4px;
  height: 22px;
  width: 22px;
  box-shadow: ${({ $selected, theme }) =>
    $selected ? `0 0 0 2px ${theme.colors.envyAlpha30}` : "none"};
  cursor: pointer;
  padding: 0;
  transition: border-color 0.15s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.envy};
  }
`;

export const Footer = styled(Flex)`
  align-items: center;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
`;

export const AllSettingsLink = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: inline-flex;
  font-family: Barlow;
  font-size: 14px;
  font-weight: 500;
  gap: 2px;
  margin-right: auto;
  padding: 0;
  transition: color 0.15s;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }
`;

export const SaveFeedback = styled(Flex)<{ $success: boolean }>`
  align-items: center;
  color: ${({ $success }) => ($success ? "#4dbd94" : "#e74c3c")};
  display: inline-flex;
  font-size: 16px;
  font-weight: 600;
  gap: 4px;
`;

export const SaveButton = styled.button`
  background: ${({ theme }) => theme.colors.envy};
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  font-family: "Barlow", sans-serif;
  font-size: 17px;
  font-weight: 600;
  padding: 10px 20px;
  transition:
    opacity 0.2s,
    transform 0.1s;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const CodeField = styled(Box)`
  margin: 4px 0 0 34px;
  max-width: 100%;
  width: calc(100% - 34px);
`;

export const CodeHint = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-family: Barlow;
  font-size: 14px;
  margin-top: 6px;
`;

export const CodeInput = styled.input`
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 6px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 15px;
  margin-bottom: 8px;
  padding: 8px 10px;
  width: 100%;

  &:focus {
    border-color: ${({ theme }) => theme.colors.envy};
    outline: none;
  }
`;

export const PreviewColumn = styled(Flex)`
  align-self: flex-start;
  flex: 0 0 576px;
  flex-direction: column;

  @media (max-width: 860px) {
    align-self: stretch;
    flex: 0 0 auto;
    order: -1;
    width: 100%;
  }
`;

// Fixed 16:9 box. Explicit height rather than aspect-ratio so flex-shrink inside
// the modal cannot squash it and throw the clip-path regions out of line.
export const Preview = styled(Box)`
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 8px;
  cursor: zoom-in;
  flex: 0 0 auto;
  height: 324px;
  overflow: hidden;
  position: relative;
  width: 100%;

  @media (max-width: 860px) {
    height: auto;
    padding-top: 56.25%;
  }
`;

export const PreviewNote = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-family: Barlow;
  font-size: 15px;
  line-height: 1.5;
  margin-top: 12px;
`;

// Wraps the layer stack so a hover magnifier can scale them together.
export const PreviewStage = styled.div`
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  transition: transform 0.15s ease-out;
  width: 100%;
`;

export const PreviewTint = styled.div<{ $mask: string; $inset: string }>`
  background: currentColor;
  inset: ${({ $inset }) => $inset};
  mask: url(${({ $mask }) => $mask}) center / 100% 100% no-repeat;
  -webkit-mask: url(${({ $mask }) => $mask}) center / 100% 100% no-repeat;
  mix-blend-mode: color;
  pointer-events: none;
  position: absolute;
`;

export const PreviewLayer = styled.img<{ $clip?: string }>`
  height: 100%;
  left: 0;
  object-fit: fill;
  position: absolute;
  top: 0;
  width: 100%;
  ${({ $clip }) => ($clip ? `clip-path: inset(${$clip});` : "")}
`;

export const PresetRow = styled(Flex)`
  gap: 8px;
  padding-bottom: 6px;
`;

export const PresetButton = styled.button`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  font-family: Barlow;
  font-size: 15px;
  font-weight: 500;
  padding: 7px 16px;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.envy};
    color: ${({ theme }) => theme.colors.ice};
  }
`;

export const Layout = styled(Flex)`
  align-items: flex-start;
  gap: 20px;

  @media (max-width: 860px) {
    flex-direction: column;
  }
`;

export const TabBar = styled(Flex)`
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 8px;
  gap: 4px;
  margin-bottom: 20px;
  padding: 4px;
`;

export const NavItem = styled.button<{ $active: boolean }>`
  align-items: center;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.envy : "transparent"};
  border: none;
  border-radius: 6px;
  color: ${({ $active, theme }) => ($active ? "white" : theme.colors.ice)};
  cursor: pointer;
  display: flex;
  flex: 1;
  font-family: Barlow;
  font-size: 16px;
  font-weight: 600;
  gap: 8px;
  justify-content: center;
  padding: 10px 14px;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ $active, theme }) =>
      $active ? theme.colors.envy : theme.colors.chalkAlpha10};
  }
`;

export const Controls = styled(Box)`
  flex: 1;
  overflow-x: hidden;
  max-height: min(440px, 60vh);
  min-width: 0;
  overflow-y: auto;
  padding-right: 10px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.chalkAlpha20};
    border-radius: 2px;
  }
`;

export const OptionRow = styled.button<{ $selected: boolean }>`
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  gap: 12px;
  padding: 12px 0;
  text-align: left;
  width: 100%;

  &:hover [data-radio] {
    border-color: ${({ theme }) => theme.colors.envy};
  }
`;

export const RadioDot = styled(Box)<{ $selected: boolean }>`
  background: ${({ $selected, theme }) =>
    $selected ? theme.colors.envy : "transparent"};
  border: 2px solid
    ${({ $selected, theme }) =>
      $selected ? theme.colors.envy : theme.colors.chalkAlpha20};
  border-radius: 50%;
  flex-shrink: 0;
  height: 12px;
  width: 12px;
`;

export const HeaderText = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const Subtitle = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-family: Barlow;
  font-size: 15px;
`;

export const Toggle = styled(Flex)<{ $on: boolean }>`
  align-items: center;
  background: ${({ $on, theme }) =>
    $on ? theme.colors.envy : theme.colors.chalkAlpha20};
  border-radius: 999px;
  cursor: pointer;
  flex-shrink: 0;
  height: 26px;
  padding: 3px;
  transition: background 0.18s ease;
  width: 46px;
`;

export const ToggleKnob = styled(Box)<{ $on: boolean }>`
  background: ${({ theme }) => theme.colors.ice};
  border-radius: 50%;
  height: 20px;
  transform: translateX(${({ $on }) => ($on ? "20px" : "0")});
  transition: transform 0.18s ease;
  width: 20px;
`;

export const SectionHeader = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  font-family: Barlow;
  font-size: 12px;
  font-weight: 600;
  gap: 6px;
  letter-spacing: 0.8px;
  margin-bottom: 4px;
  margin-top: 12px;
  text-transform: uppercase;
`;

export const Divider = styled(Box)`
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  height: 1px;
  margin: 12px 0 4px;
  width: 100%;
`;

export const Select = styled.select`
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  flex-shrink: 0;
  font-family: Barlow;
  font-size: 15px;
  padding: 6px 10px;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.envy};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.envy};
    outline: none;
  }

  option {
    background: ${({ theme }) => theme.colors.ned};
    color: ${({ theme }) => theme.colors.ice};
  }
`;

export const SectionIntro = styled(Text)<{ $bright?: boolean }>`
  color: ${({ theme, $bright }) =>
    $bright ? theme.colors.ice : theme.colors.chalk};
  font-family: Barlow;
  font-size: 15px;
  line-height: 1.4;
  margin-bottom: 6px;
`;

export const ImportArea = styled.textarea`
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 8px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.ice};
  font-family: monospace;
  font-size: 13px;
  line-height: 1.6;
  min-height: 140px;
  padding: 12px 14px;
  resize: vertical;
  width: 100%;

  &::placeholder {
    color: ${({ theme }) => theme.colors.chalkAlpha40};
    font-family: Barlow;
    font-size: 14px;
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.envy};
    outline: none;
  }
`;

export const ImportActions = styled(Flex)`
  align-items: center;
  gap: 12px;
  margin-top: 10px;
`;

export const ImportButton = styled.button`
  align-items: center;
  background: transparent;
  display: inline-flex;
  gap: 6px;
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  font-family: Barlow;
  font-size: 15px;
  font-weight: 500;
  padding: 7px 16px;
  transition: all 0.15s ease;

  &:disabled {
    cursor: default;
    opacity: 0.4;
  }

  &:not(:disabled):hover {
    border-color: ${({ theme }) => theme.colors.envy};
    color: ${({ theme }) => theme.colors.ice};
  }
`;

export const ImportResult = styled(Text)<{ $found: boolean }>`
  align-items: center;
  color: ${({ theme, $found }) =>
    $found ? theme.colors.envy : theme.colors.caution};
  display: flex;
  font-family: Barlow;
  font-size: 14px;
  font-weight: ${({ $found }) => ($found ? 400 : 600)};
  gap: 6px;
  line-height: 1.4;
  text-align: left;
`;

export const ClearButton = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  font-family: Barlow;
  font-size: 13px;
  padding: 0;
  text-decoration: underline;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }
`;

// A label rather than a button, so it can drive a hidden file input.
export const ImportFileButton = styled.label`
  align-items: center;
  background: transparent;
  display: inline-flex;
  gap: 6px;
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  font-family: Barlow;
  font-size: 15px;
  font-weight: 500;
  padding: 7px 16px;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.envy};
    color: ${({ theme }) => theme.colors.ice};
  }

  input {
    display: none;
  }
`;

export const RangeRow = styled(Flex)<{ $disabled?: boolean }>`
  align-items: center;
  gap: 16px;
  justify-content: space-between;
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  padding: 12px 0;
  transition: opacity 0.15s ease;
  width: 100%;
`;

export const RangeControls = styled(Flex)`
  align-items: center;
  flex: 0 0 auto;
  gap: 10px;
`;

// Tabular figures so the row does not jitter as the number changes width.
export const RangeValue = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-family: Barlow;
  font-size: 15px;
  font-variant-numeric: tabular-nums;
  min-width: 52px;
  text-align: right;
`;

export const RangeInput = styled.input`
  accent-color: ${({ theme }) => theme.colors.envy};
  cursor: pointer;
  width: 160px;

  &:disabled {
    cursor: default;
  }
`;

export const TipBanner = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  border: none;
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: flex;
  font-family: Barlow;
  font-size: 15px;
  gap: 10px;
  margin-bottom: 16px;
  padding: 12px 14px;
  text-align: left;
  transition: background 0.15s ease;
  width: 100%;

  &:hover {
    background: ${({ theme }) => theme.colors.chalkAlpha20};
  }
`;

export const TipLabel = styled.span`
  color: ${({ theme }) => theme.colors.envy};
  font-weight: 600;
`;

export const TipAction = styled.span`
  color: ${({ theme }) => theme.colors.envy};
  font-weight: 600;
  text-decoration: underline;
`;

export const DropZone = styled(Flex)<{ $active: boolean }>`
  align-items: center;
  background: ${({ theme, $active }) =>
    $active ? theme.colors.envyAlpha30 : theme.colors.chalkAlpha10};
  border: 2px dashed
    ${({ theme, $active }) =>
      $active ? theme.colors.envy : theme.colors.chalkAlpha20};
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.chalk};
  flex-direction: column;
  gap: 6px;
  padding: 24px 16px;
  text-align: center;
  transition: all 0.15s ease;
  width: 100%;
`;

export const DropTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 17px;
  font-weight: 500;
`;

export const DropHint = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-family: Barlow;
  font-size: 15px;
  line-height: 1.5;
  max-width: 420px;
`;

export const PasteToggle = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  font-family: Barlow;
  font-size: 14px;
  margin-top: 10px;
  padding: 0;
  text-decoration: underline;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }
`;

export const LocationRow = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  gap: 10px;
  padding: 4px 0;
`;

export const LocationOs = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  flex: 0 0 96px;
  font-family: Barlow;
  font-size: 14px;
  gap: 6px;
`;

export const LocationNote = styled(Box)`
  color: ${({ theme }) => theme.colors.chalk};
  display: grid;
  font-family: Barlow;
  font-size: 13px;
  gap: 5px;
  line-height: 1.5;
  max-width: 72ch;
  padding: 8px 0 2px;
`;

export const LocationId = styled.span`
  border-bottom: 1px dashed ${({ theme }) => theme.colors.chalkAlpha40};
  color: ${({ theme }) => theme.colors.chalk};
  font-family: monospace;
`;

export const LocationPath = styled(Box)`
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.caution};
  flex: 0 1 auto;
  max-width: 100%;
  font-family: monospace;
  font-size: 13px;
  min-width: 0;
  overflow-x: auto;
  padding: 5px 9px;
  white-space: pre;
`;

export const LoadedFiles = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  flex-wrap: wrap;
  font-family: Barlow;
  font-size: 14px;
  gap: 8px;
  justify-content: center;
`;

export const PreviewCaveat = styled(Text)`
  color: ${({ theme }) => theme.colors.caution};
  font-family: Barlow;
  font-size: 14px;
  line-height: 1.4;
  padding: 2px 0 10px;
`;

export const FileRow = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.envy};
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px 0 2px;
`;

export const FileName = styled.span`
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.envy};
  font-family: monospace;
  font-size: 13px;
  padding: 3px 7px;
  white-space: nowrap;
`;

export const MatchedConvars = styled(Box)`
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.chalk};
  font-family: Barlow;
  font-size: 15px;
  line-height: 1.6;
  max-height: 180px;
  overflow: auto;
  padding: 10px 12px;
`;

export const MatchedRow = styled(Flex)`
  align-items: center;
  gap: 12px;
  justify-content: space-between;
`;

export const MatchedValue = styled.span`
  color: ${({ theme }) => theme.colors.envy};
  font-variant-numeric: tabular-nums;
`;

export const CollapseHeader = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: flex;
  font-family: Barlow;
  font-size: 12px;
  font-weight: 600;
  gap: 6px;
  letter-spacing: 0.8px;
  margin: 12px 0 4px;
  padding: 0;
  text-transform: uppercase;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }
`;

// No border: the drop zone above already ends in a dashed edge, and a second
// rule right under it reads as one connected block rather than two.
export const ImportSection = styled(Box)`
  margin-top: 22px;
  width: 100%;
`;

export const MatchedEmpty = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.caution};
  font-weight: 600;
  gap: 6px;
`;

export const MatchedLabel = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.envy};
  gap: 8px;
`;
